import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../NotificationBell/NotificationBell';
import Modal from '../Modal/Modal';
import api from '../../api/axios';
import './Sidebar.css';

const roleLabels = {
  super_admin: 'Super Administrator',
  customer_admin: 'Kundenadmin',
  employee: 'Mitarbeiter',
};

const navItems = {
  super_admin: [
    { to: '/admin/dashboard', label: 'Dashboard', icon: '▦' },
    { to: '/admin/customers', label: 'Kunden', icon: '🏢' },
    { to: '/admin/trainings', label: 'Schulungen', icon: '📚' },
    { to: '/admin/billing', label: 'Abrechnung', icon: '💶' },
  ],
  customer_admin: [
    { to: '/customer/dashboard', label: 'Dashboard', icon: '▦' },
    { to: '/customer/users', label: 'Mitarbeiter', icon: '👥' },
    { to: '/customer/trainings', label: 'Schulungen', icon: '📚' },
    { to: '/customer/reports', label: 'Berichte', icon: '📊' },
    { to: '/customer/billing', label: 'Abrechnung', icon: '💶' },
    { to: '/my-trainings', label: 'Meine Schulungen', icon: '🎓' },
  ],
  employee: [
    { to: '/my-trainings', label: 'Meine Schulungen', icon: '🎓' },
  ],
};

export default function Sidebar({ open = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = navItems[user?.role] || [];

  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  function openPwModal() {
    setPwForm({ current: '', next: '', confirm: '' });
    setPwError('');
    setPwSuccess('');
    setPwModal(true);
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pwForm.next !== pwForm.confirm) {
      setPwError('Neue Passwörter stimmen nicht überein');
      return;
    }
    if (pwForm.next.length < 6) {
      setPwError('Neues Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: pwForm.current,
        newPassword: pwForm.next,
      });
      setPwSuccess('Passwort erfolgreich geändert');
      setTimeout(() => setPwModal(false), 1500);
    } catch (err) {
      setPwError(err.response?.data?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setPwLoading(false);
    }
  }

  return (
    <>
      {open && <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />}
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img src="/images/logo.svg" alt="Logo" className="sidebar-logo" />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <NotificationBell />
            <button className="sidebar-icon-btn sidebar-close" onClick={onClose} aria-label="Menü schließen">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          {items.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-legal">
          <a href="/impressum" target="_blank" rel="noreferrer">Impressum</a>
          <span>·</span>
          <a href="/datenschutz" target="_blank" rel="noreferrer">Datenschutz</a>
          <span>·</span>
          <a href="mailto:info@ff-ai-automation.de">Kontakt</a>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{user?.name}</span>
              <span className="sidebar-user-role">{roleLabels[user?.role]}</span>
            </div>
          </div>
          <div className="sidebar-footer-actions">
            <button className="sidebar-icon-btn" onClick={openPwModal} title="Passwort ändern">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </button>
            <button className="sidebar-logout" onClick={logout} title="Abmelden">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      <Modal isOpen={pwModal} onClose={() => setPwModal(false)} title="Passwort ändern" size="sm">
        <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Aktuelles Passwort</label>
            <input
              type="password"
              className="form-input"
              value={pwForm.current}
              onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label">Neues Passwort</label>
            <input
              type="password"
              className="form-input"
              value={pwForm.next}
              onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Neues Passwort bestätigen</label>
            <input
              type="password"
              className="form-input"
              value={pwForm.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
              required
            />
          </div>
          {pwError && <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 0 }}>{pwError}</p>}
          {pwSuccess && <p style={{ color: 'var(--success)', fontSize: '0.875rem', margin: 0 }}>{pwSuccess}</p>}
          <button type="submit" className="btn btn-primary" disabled={pwLoading}>
            {pwLoading ? 'Wird gespeichert…' : 'Passwort ändern'}
          </button>
        </form>
      </Modal>
    </>
  );
}